/**
 * The two visibility switches of a company, a point of sale or a depot.
 *
 * "Customers" is whether customers see it; "Back office" is whether it shows in the filters,
 * the top bar and the stock screens of the people who run the installation. Two switches
 * because they answer two questions, and either can be off without the other: a depot hidden
 * from customers is often somebody's daily work.
 *
 * A row can also be hidden **by what it belongs to** (its company, its point of sale, or because
 * everything it hosts is hidden) while its own switch is on. That is said on the row, so nobody
 * flips a switch that is already on and wonders why nothing changed; the switch itself still
 * shows the item's own setting, because that is what the button changes.
 */

import { useTranslation } from "react-i18next";

import { NAMESPACE } from "../../i18n";

export type Audience = "customers" | "backOffice";

export interface Switch {
  /** This item's own switch. */
  visible: boolean;
  /** Hidden because of something above it, whatever its own switch says. */
  hiddenByParent: boolean;
}

export interface VisibilityControlsProps {
  customers: Switch;
  backOffice: Switch;
  /** Which kind of parent can hide it: how the "hidden by" hint is worded. */
  parent: "company-or-point" | "depots";
  /** False for somebody who may read but not write: the state is shown, with no buttons. */
  editable: boolean;
  isBusy: (audience: Audience) => boolean;
  onToggle: (audience: Audience, visible: boolean) => void;
}

export function VisibilityControls({
  customers,
  backOffice,
  parent,
  editable,
  isBusy,
  onToggle,
}: VisibilityControlsProps) {
  const { t } = useTranslation(NAMESPACE);

  // One pill per audience, side by side: who it is about, and in what state. The pill is the
  // control when the reader may write, so there is no second button to look for. Three states,
  // three tones: shown (green), hidden here (grey), hidden by what it belongs to (amber, with
  // the reason in the tooltip) — never the same picture for two different reasons.
  const pill = (audience: Audience, state: Switch) => {
    const name = t(audience === "customers" ? "visibility.customers" : "visibility.backOffice");
    const shown = state.visible && !state.hiddenByParent;
    const byParent = state.visible && state.hiddenByParent;
    const word = shown
      ? t("visibility.visible")
      : byParent
        ? t("visibility.hiddenShort")
        : t("visibility.hidden");
    const reason = byParent
      ? t(parent === "depots" ? "visibility.byParent.depots" : "visibility.byParent.company")
      : undefined;
    const tone = shown ? "shown" : byParent ? "inherited" : "hidden";
    const content = (
      <>
        <span className="hardauto-visibility__dot" aria-hidden="true" />
        <span className="hardauto-visibility__name">{name}</span>
        <span className="hardauto-visibility__state">{word}</span>
      </>
    );
    if (!editable) {
      return (
        <span
          className="hardauto-visibility__pill"
          data-tone={tone}
          data-audience={audience}
          title={reason}
        >
          {content}
        </span>
      );
    }
    const action = state.visible ? t("visibility.hide") : t("visibility.show");
    return (
      <button
        type="button"
        className="hardauto-visibility__pill"
        data-tone={tone}
        data-audience={audience}
        data-busy={isBusy(audience) ? "true" : undefined}
        disabled={isBusy(audience)}
        title={reason ? `${reason} — ${action}` : action}
        aria-label={`${action} (${name})`}
        onClick={() => onToggle(audience, !state.visible)}
      >
        {content}
      </button>
    );
  };

  return (
    <div className="hardauto-visibility">
      {pill("customers", customers)}
      {pill("backOffice", backOffice)}
    </div>
  );
}
